import React, { useState, useEffect,useRef } from 'react';
import { X, Users, Save, Check, AlertCircle, CheckCircle } from 'lucide-react';

const TeamManager = ({ 
  selectedSchedule, 
  schedulesService, 
  onScheduleUpdate,
  onClose 
}) => {
  const [teams, setTeams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const teamOrderRef = useRef(new Map());

useEffect(() => {
  console.log('=== useEffect triggered ===');
  console.log('Team order ref:', teamOrderRef.current);
  
  if (selectedSchedule?.schedule_data) {
    const unique = new Set();
    selectedSchedule.schedule_data.forEach((w) =>
      w.games.forEach((g) => {
        unique.add(g.homeTeam);
        unique.add(g.awayTeam);
      })
    );
    
    const uniqueArray = Array.from(unique);
    console.log('Unique teams from schedule:', uniqueArray);
    
    // Sort teams based on persisted order, or numerically for new teams
    const sortedTeams = uniqueArray.sort((a, b) => {
      const orderA = teamOrderRef.current.get(a);
      const orderB = teamOrderRef.current.get(b);
      
      // If both teams have a persisted order, use it
      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB;
      }
      
      // If only one has persisted order, it comes first
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
      
      // For new teams without persisted order, sort numerically
      const numA = parseInt(a.match(/\d+/)?.[0] || '0');
      const numB = parseInt(b.match(/\d+/)?.[0] || '0');
      
      if (numA && numB) {
        return numA - numB;
      }
      
      return a.localeCompare(b);
    });
    
    console.log('Sorted teams:', sortedTeams);
    
    // Store the order for each team (if not already stored)
    sortedTeams.forEach((t, index) => {
      if (!teamOrderRef.current.has(t)) {
        teamOrderRef.current.set(t, index);
        console.log(`Stored order for ${t}: ${index}`);
      }
    });
    
    setTeams(sortedTeams.map((t) => ({ 
      original: t, 
      new: t, 
      sortOrder: teamOrderRef.current.get(t) 
    })));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedSchedule]);


  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      // Check for duplicates
      const newNames = teams.map(t => t.new.toLowerCase().trim());
      const duplicates = newNames.filter((name, index) => newNames.indexOf(name) !== index);
      if (duplicates.length > 0) {
        setError('Team names must be unique');
        setSaving(false);
        return;
      }
      
      const map = {};
      teams.forEach((t) => {
        if (t.original !== t.new && t.new.trim() !== '') {
          map[t.original] = t.new.trim();
        }
      });

      if (Object.keys(map).length === 0) {
        setError('No changes to save');
        setSaving(false);
        return;
      }

      const updated = selectedSchedule.schedule_data.map((w) => ({
        ...w,
        games: w.games.map((g) => ({
          ...g,
          homeTeam: map[g.homeTeam] || g.homeTeam,
          awayTeam: map[g.awayTeam] || g.awayTeam
        }))
      }));

    await schedulesService.update(selectedSchedule.id, {
  teamNamesOnly: true,
  teamNameMapping: map,
  scheduleData: updated
});

// Update the teamOrderRef with the new names
console.log('Updating teamOrderRef with new mappings:', map);
Object.entries(map).forEach(([oldName, newName]) => {
  const order = teamOrderRef.current.get(oldName);
  if (order !== undefined) {
    teamOrderRef.current.delete(oldName);
    teamOrderRef.current.set(newName, order);
    console.log(`Moved order: ${oldName} (${order}) -> ${newName} (${order})`);
  }
});
console.log('Updated teamOrderRef:', teamOrderRef.current);

await onScheduleUpdate();
      
      // Update local state to reflect saved changes
      setTeams(prev => prev.map(t => ({ original: t.new, new: t.new, sortOrder: t.sortOrder })));
      
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      setError('Failed to update team names. Please try again.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = teams.some(t => t.original !== t.new);

  return (
    <div style={{
      width: '100%',
      height: '600px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '8px',
      position: 'relative'
    }}>
      {/* Success Message */}
      {showSuccess && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 2000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: '500' }}>Team names updated successfully!</span>
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Users size={24} color="#3b82f6" />
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Team Manager</h2>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '4px'
          }}
        >
          <X size={20} />
        </button>
      </div>

      {/* Instruction */}
      <div style={{
        padding: '16px 20px',
        backgroundColor: '#eff6ff',
        borderBottom: '1px solid #e5e7eb',
        fontSize: '14px',
        color: '#1e40af'
      }}>
        Rename teams across the entire schedule. Changes apply to all games.
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          margin: '16px 20px 0',
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#dc2626'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '20px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '16px'
        }}>
          {teams.map((team) => {
            const hasChange = team.original !== team.new;
            
            return (
              <div key={team.original} style={{
                padding: '16px',
                backgroundColor: hasChange ? '#eff6ff' : '#f8fafc',
                border: hasChange ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Original Name
                </label>
                
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  {team.original}
                </div>
                
                <label style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  New Name
                </label>
                
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={team.new}
                    onChange={(e) =>
                      setTeams((prev) =>
                        prev.map((tm) =>
                          tm.original === team.original ? { ...tm, new: e.target.value } : tm
                        )
                      )
                    }
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      paddingRight: hasChange ? '36px' : '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {hasChange && (
                    <div style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}>
                      <Check size={16} color="#10b981" />
                    </div>
                  )}
                </div>
                
                {hasChange && (
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#059669',
                    fontWeight: '500'
                  }}>
                    → Will rename to: "{team.new}"
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Changes Summary */}
        {hasChanges && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px'
          }}>
            <h4 style={{
              margin: '0 0 12px 0',
              fontSize: '14px',
              fontWeight: '600',
              color: '#0c4a6e'
            }}>
              Pending Changes ({teams.filter(t => t.original !== t.new).length})
            </h4>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              color: '#0369a1'
            }}>
              {teams
                .filter(t => t.original !== t.new)
                .map(t => (
                  <li key={t.original} style={{ marginBottom: '4px' }}>
                    <strong>{t.original}</strong> → <strong>{t.new}</strong>
                  </li>
                ))
              }
            </ul>
          </div>
        )}
      </div>

      {/* Footer with Save Button */}
      {hasChanges && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: saving ? '#93c5fd' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!saving) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (!saving) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            <Save size={16} />
            {saving ? 'Saving...' : `Save Changes (${teams.filter(t => t.original !== t.new).length})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamManager;